import moment from 'moment';
import 'moment/locale/fr-ca';
import i18n from '../i18n';
import dayjs from 'dayjs';

export function getLocalTime(timestamp) {
  if (typeof timestamp === 'string' && timestamp.includes('-')) {
    const m = new moment(timestamp);
    return m;
  }
  const epoch = parseInt(`${timestamp}`);
  const m = new moment(epoch);
  const currentLanguage = i18n.resolvedLanguage;
  moment.locale(currentLanguage || 'en');
  return m;
}

// Like getLocalTime, but always renders the UTC calendar date regardless of
// the viewer's browser timezone. Use for dates that represent a fixed
// calendar day (e.g. a report's scheduled date) rather than a moment in time.
export function getCalendarDate(timestamp) {
  if (typeof timestamp === 'string' && timestamp.includes('-')) {
    return moment.utc(timestamp);
  }
  const epoch = parseInt(`${timestamp}`);
  const m = moment.utc(epoch);
  const currentLanguage = i18n.resolvedLanguage;
  moment.locale(currentLanguage || 'en');
  return m;
}

export async function sleep(milliseconds = 2500) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, milliseconds);
  });
}
export function getDayDescription(timestamp) {
  const time = getLocalTime(timestamp);
  const now = moment();

  if (time.isSame(now, 'day')) {
    return 'Today';
  } else if (time.isSame(now.clone().subtract(1, 'days'), 'day')) {
    return 'Yesterday';
  } else {
    return time.format('DD/MM/YYYY');
  }
}

export function getDaysFromNow(date) {
  const endDate = getLocalTime(date);
  let diff = endDate.diff(moment(), 'days');
  let string = 'days';
  if (diff === 0) {
    diff = endDate.diff(moment(), 'hours');
    string = 'hours';
  }
  if (diff === 1) {
    string = 'day';
  }

  return `${diff < 0 ? 'Overdue' : 'Due in'} ${Math.abs(diff)} ${string}`;
}

export function fromNowDateOnly(dateStr) {
  const today = dayjs().startOf('day');
  const inputDay = dayjs(dateStr).startOf('day');
  const diff = today.diff(inputDay, 'day');

  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff === -1) return 'Tomorrow';
  if (diff > 1) return `${diff} days ago`;
  if (diff < -1) return `In ${Math.abs(diff)} days`;
}
export function formatTime(date, customOptions = {}) {
  let options = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };
  let newDate = new Date(date);
  return newDate.toLocaleDateString(
    i18n.resolvedLanguage,
    customOptions || options,
  );
}
export function getOrdinalWeekdayLabel(date) {
  const m = moment(date);
  if (!m.isValid()) return '';
  const isLast = m.clone().add(7, 'days').month() !== m.month();
  const ordinal = isLast
    ? 'last'
    : ['1st', '2nd', '3rd', '4th'][Math.ceil(m.date() / 7) - 1];
  return `the ${ordinal} ${m.format('dddd')}`;
}

export const monthSelector = [
  { label: 'January', value: '01' },
  { label: 'February', value: '02' },
  { label: 'March', value: '03' },
  { label: 'April', value: '04' },
  { label: 'May', value: '05' },
  { label: 'June', value: '06' },
  { label: 'July', value: '07' },
  { label: 'August', value: '08' },
  { label: 'September', value: '09' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
];
export function daysSince(timestamp, now = Date.now()) {
  const diff = now - timestamp;
  if (diff <= 0) return 0;
  return Math.ceil(diff / 86400000);
}
export function dayDelta(dueAt, now = Date.now()) {
  const MS_DAY = 24 * 60 * 60 * 1000;
  const dueMs = dueAt instanceof Date ? dueAt.getTime() : Number(dueAt);
  if (!Number.isFinite(dueMs)) return null;
  const diff = dueMs - now;
  return diff >= 0
    ? Math.ceil(diff / MS_DAY)
    : -Math.ceil(Math.abs(diff) / MS_DAY);
}
export function fromNowOrToday(ts) {
  const m = toMoment(ts);
  if (!m.isValid()) return '';

  // Ensure a valid moment locale; fall back to 'en' if your i18n code uses an unsupported tag
  const loc = (i18n?.resolvedLanguage || 'en').toLowerCase();
  moment.locale(moment.locales().includes(loc) ? loc : 'en');

  if (m.isSame(moment(), 'day')) return 'Today';
  if (m.isSame(moment().add(1, 'day'), 'day')) return 'Tomorrow';
  if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
  return m.fromNow();
}

function toMoment(timestamp) {
  if (typeof timestamp === 'string' && timestamp.includes('-')) {
    return moment(timestamp);
  }
  const n = Number(timestamp);
  if (!Number.isFinite(n)) return moment.invalid();
  // Detect seconds vs ms
  const ms = n < 1e12 ? n * 1000 : n;
  return moment(ms);
}

let _pdfTimeSetting = 'standard';
export const setPdfTimeSetting = (s) => {
  _pdfTimeSetting = s || 'standard';
};
export const getPdfTimeSetting = () => _pdfTimeSetting;
